import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { App } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import possibleTypes from "../gql/generated/possibleTypes.tsx";
import { routes } from "../routes.ts";

const AUTH_ERROR_CODES = new Set(["error.auth.not-authenticated", "error.auth.not-authorized"]);

const hasServiceError = (errors: readonly { extensions?: Record<string, unknown> }[]) =>
  errors.some((e) => e.extensions?.["errorCode"]);

const hasAuthError = (errors: readonly { extensions?: Record<string, unknown> }[]) =>
  errors.some((e) => {
    const code = e.extensions?.["errorCode"];
    return typeof code === "string" && AUTH_ERROR_CODES.has(code);
  });

export const useApolloClient = () => {
  const { modal } = App.useApp();
  const { t } = useTranslation();

  const apolloClient = useMemo(() => {
    console.log("Creating Apollo client");

    const errorLink = new ErrorLink(({ error }) => {
      if (CombinedGraphQLErrors.is(error)) {
        error.errors.forEach(({ message, locations, path }) =>
          console.log(`[GraphQL error]: Message: ${message}, Location: `, locations, ` Path: ${path}`),
        );
        // Backend clears the session cookie when it sees a userId without a team_member
        // row, so the response Set-Cookie has already wiped us by the time JS sees the
        // body. Bounce to login — full navigation, no modal.
        if (hasAuthError(error.errors)) {
          if (window.location.pathname !== routes.login.to()) {
            window.location.assign(routes.login.to());
          }
          return;
        }
        if (hasServiceError(error.errors)) return;

        modal.error({
          title: t("error.application-error.title"),
          content: t("error.application-error.description"),
          onOk: () => {
            window.location.href = "/";
          },
          okText: t("g.reload"),
        });
      } else if (CombinedProtocolErrors.is(error)) {
        error.errors.forEach(({ message, extensions }) =>
          console.log(`[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`),
        );
        modal.error({
          title: t("error.application-error.title"),
          content: t("error.application-error.description"),
          onOk: () => {
            window.location.href = "/";
          },
          okText: t("g.reload"),
        });
      } else {
        console.error(`[Network error]: ${error}`);
        modal.error({
          title: t("error.network-error.title"),
          content: t("error.network-error.description"),
          onOk: () => {
            window.location.href = "/";
          },
          okText: t("g.reload"),
          closable: true,
        });
      }
    });

    const httpLink = new HttpLink({
      uri: import.meta.env.VITE_GRAPHQL_ENDPOINT,
    });

    return new ApolloClient({
      link: ApolloLink.from([errorLink, httpLink]),
      cache: new InMemoryCache({
        possibleTypes: possibleTypes.possibleTypes,
      }),
      defaultOptions: {
        query: { fetchPolicy: "no-cache" },
        mutate: { fetchPolicy: "no-cache" },
        watchQuery: { fetchPolicy: "no-cache" },
      },
    });
  }, [modal, t]);

  return { apolloClient };
};
